import styled, { css } from 'styled-components';
import Colours from '/shared/styled/Colours';
import measureText from '/shared/functions/measureText';
import StyledSelect from './StyledSelect';

interface Props extends React.SelectHTMLAttributes<HTMLSelectElement> {
  $displayValue?: string | null;
  $baseMultiplier?: number;
  $isErrored?: boolean;
  $isDropDownPlaceholder?: boolean;
  $dropdownSingleNoBorder?: boolean;
  $hasGrayBorder?: boolean;
  $hasDropdownFirst?: boolean;
}

function calculateTextWidth(value: string, baseMultiplier = 4) {
  return Math.ceil((measureText(value) * 1.25 + baseMultiplier) * 5) * 3;
}

function textWidthStyle({ value, $displayValue = '', $baseMultiplier = 4 }: Props) {
  if ($displayValue && $displayValue?.length > 0) {
    return css`
      width: ${calculateTextWidth($displayValue, $baseMultiplier)}px;
    `;
  }
  if (value && value.toString()?.length > 0) {
    return css`
      width: ${calculateTextWidth(value.toString(), $baseMultiplier)}px;
    `;
  }
}

function disabledStyle({ disabled }: Props) {
  if (disabled) {
    return css`
      border-color: ${Colours.greyLight6};
      background: ${Colours.whiteDark1};
      color: ${Colours.greyLight1};
      opacity: 0.8;
      cursor: not-allowed;

      &::-webkit-input-placeholder {
        color: ${Colours.greyLight1} !important;
      }

      &:-ms-input-placeholder {
        color: ${Colours.greyLight1} !important;
      }
    `;
  }
}

function placeholderStyle({ $isDropDownPlaceholder = false }: Props) {
  if ($isDropDownPlaceholder) {
    return css`
      color: ${Colours.grey};
    `;
  }
}

function errorStyle({ $isErrored = false }: Props) {
  if ($isErrored) {
    return css`
      &,
      &:focus,
      &:active {
        border-color: ${Colours.redLight5};
        z-index: 2;
      }
    `;
  }
  return css`
    &,
    &:focus,
    &:active {
      border-color: ${Colours.greyLight2};
      z-index: 2;
    }
  `;
}

function dropdownSingleNoBorderStyle({ $dropdownSingleNoBorder = false }: Props) {
  if ($dropdownSingleNoBorder) {
    return css`
      border: 0;
      padding-right: 0;
      margin-left: 0;
      width: auto;
    `;
  }
}

function hasGrayBorderStyle({ $hasGrayBorder = false }: Props) {
  if ($hasGrayBorder) {
    return css`
      &:focus,
      &:active {
        border-color: ${Colours.greyLight2};
      }
    `;
  }
}

function hasDropdownFirst({ $hasDropdownFirst = false }: Props) {
  if ($hasDropdownFirst) {
    return css`
      border-radius: 1.5rem;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0;
    `;
  }
}

export default styled(StyledSelect)<Props>`
  background-color: ${Colours.white};
  padding-right: 2.6rem;
  padding-left: 1.2rem;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  line-height: 1.5;
  flex-shrink: 0;
  margin-left: -1px;
  width: 8rem;
  min-width: 0;

  > span {
    min-width: 2rem;
    display: inline-block;
  }

  &::-ms-expand {
    display: none;
  }
  ${hasGrayBorderStyle}
  ${textWidthStyle}
  ${errorStyle}
  ${placeholderStyle}
  ${disabledStyle}
  ${dropdownSingleNoBorderStyle}
  ${hasDropdownFirst}
`;
